import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  DocumentTextIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  PaperClipIcon,
  CalendarDaysIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';

interface PolicyDocument {
  id: number;
  policy_id: number;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  description: string | null;
  uploaded_by: string;
  created_at: string;
}

interface PolicyDocumentsTabProps {
  policyId: number;
  onDocumentChange?: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const PolicyDocumentsTab: React.FC<PolicyDocumentsTabProps> = ({
  policyId,
  onDocumentChange,
}) => {
  const [documents, setDocuments] = useState<PolicyDocument[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<PolicyDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!policyId || isNaN(policyId)) return;

    setLoadingDocuments(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/policy-documents/${policyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const payload = response.data;
      const list: PolicyDocument[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setDocuments(list);
    } catch (err: any) {
      console.error('❌ Error fetching policy documents:', err);
      setError(err.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  }, [policyId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file && !description) {
      setDescription(file.name.split('.')[0].replace(/[_-]/g, ' '));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('description', description || selectedFile.name);

      await axios.post(
        `${API_URL}/api/policy-documents/${policyId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(
                Math.round((progressEvent.loaded * 100) / progressEvent.total)
              );
            }
          },
        }
      );

      setSuccess('Document uploaded successfully!');
      setSelectedFile(null);
      setDescription('');
      setUploadProgress(0);
      await fetchDocuments();
      onDocumentChange?.();
    } catch (err: any) {
      console.error('❌ Error uploading policy document:', err);
      setError(err.response?.data?.message || 'Failed to upload document');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (doc: PolicyDocument) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${API_URL}/api/policy-documents/download/${doc.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading policy document:', err);
      setError('Failed to download document');
    }
  };

  const handleViewFile = async (doc: PolicyDocument) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      setIsPreviewLoading(true);
      setPreviewDocument(doc);

      const response = await axios.get(
        `${API_URL}/api/policy-documents/download/${doc.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      setPreviewUrl(URL.createObjectURL(response.data));
    } catch (err: any) {
      console.error('Error previewing policy document:', err);
      setError('Failed to load document preview');
      setPreviewDocument(null);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const closePreviewModal = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDocument(null);
    setPreviewUrl('');
  };

  const handleDelete = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setIsDeleting(documentId);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      await axios.delete(`${API_URL}/api/policy-documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess('Document deleted successfully!');
      await fetchDocuments();
      onDocumentChange?.();
    } catch (err: any) {
      console.error('❌ Error deleting policy document:', err);
      setError(err.response?.data?.message || 'Failed to delete document');
    } finally {
      setIsDeleting(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getDocumentIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  return (
    <div className="space-y-6">
      <h4 className="text-lg font-semibold text-gray-800">Policy Documents</h4>
      <p className="text-sm text-gray-500">Manage documents for this policy</p>

      {/* Upload Form */}
      <div className="bg-gradient-to-br from-white to-blue-50 p-6 rounded-xl shadow-lg border border-blue-100">
        <div className="flex items-center mb-6">
          <div className="bg-blue-100 p-2 rounded-lg mr-3">
            <DocumentIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Upload New Document</h4>
            <p className="text-sm text-gray-500">
              Add files to this policy's document repository
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <XMarkIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter document description"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="file" className="block text-sm font-medium text-gray-700">
              Select File <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
              <div className="space-y-2 text-center">
                <PaperClipIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file"
                      type="file"
                      onChange={handleFileSelect}
                      className="sr-only"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt,text/plain"
                      required
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PDF, JPG, PNG, DOC, DOCX, TXT up to 10MB
                </p>
                {selectedFile && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center">
                      <DocumentTextIcon className="w-5 h-5 text-blue-500 mr-2" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isLoading && uploadProgress > 0 && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Uploading...</span>
                <span className="font-medium">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
            >
              {isLoading ? 'Uploading...' : (
                <>
                  <DocumentTextIcon className="w-5 h-5 mr-2" />
                  Upload Document
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                setDescription('');
                setUploadProgress(0);
                setError('');
                setSuccess('');
              }}
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white py-3 px-4 rounded-lg hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
            >
              <XMarkIcon className="w-5 h-5 mr-2" />
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Documents List */}
      <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl shadow-lg border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-800">Uploaded Documents</h4>
            <p className="text-sm text-gray-500 mt-1">
              {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
            </p>
          </div>
          <button
            onClick={fetchDocuments}
            className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-lg transition-colors"
            disabled={loadingDocuments}
          >
            Refresh
          </button>
        </div>

        {loadingDocuments ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <DocumentTextIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No documents uploaded yet
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Upload your first document using the form above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 overflow-hidden group"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                        <span className="text-xl">{getDocumentIcon(doc.mime_type)}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <CalendarDaysIcon className="w-3 h-3 mr-1" />
                      {doc.created_at ? formatDate(doc.created_at) : 'N/A'}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-medium text-gray-900 mb-1 truncate">
                      {doc.description || doc.original_filename}
                    </h5>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center flex-1 min-w-0">
                        <PaperClipIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{doc.original_filename}</span>
                      </div>
                      <button
                        onClick={() => handleViewFile(doc)}
                        className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline flex-shrink-0"
                      >
                        View File
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {doc.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
                      </span>
                      <span className="mx-2">•</span>
                      <span>{formatFileSize(doc.file_size)}</span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="flex-1 bg-blue-50 text-blue-700 py-2 px-3 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-1 transition-colors flex items-center justify-center text-sm font-medium"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Download
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      disabled={isDeleting === doc.id}
                      className="flex-1 bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm font-medium"
                    >
                      {isDeleting === doc.id ? (
                        'Deleting...'
                      ) : (
                        <>
                          <TrashIcon className="w-4 h-4 mr-2" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={closePreviewModal}
            />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      {previewDocument.description || previewDocument.original_filename}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {previewDocument.original_filename} •{' '}
                      {formatFileSize(previewDocument.file_size)}
                    </p>
                  </div>
                  <button
                    onClick={closePreviewModal}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-2">
                  {isPreviewLoading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <p className="text-gray-600">Loading preview...</p>
                    </div>
                  ) : previewUrl ? (
                    <div className="border rounded-lg overflow-hidden bg-gray-50">
                      {previewDocument.mime_type.startsWith('image/') ? (
                        <img
                          src={previewUrl}
                          alt={previewDocument.original_filename}
                          className="max-w-full h-auto mx-auto"
                        />
                      ) : previewDocument.mime_type === 'application/pdf' ? (
                        <iframe
                          src={previewUrl}
                          title={previewDocument.original_filename}
                          className="w-full h-[70vh]"
                        />
                      ) : (
                        <div className="text-center py-16 px-4">
                          <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 mb-4">
                            Preview not available for this file type.
                          </p>
                          <button
                            onClick={() => handleDownload(previewDocument)}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                            Download to view
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <p className="text-red-600">Failed to load document preview.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => previewDocument && handleDownload(previewDocument)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  Download
                </button>
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyDocumentsTab;